import { Flex, Pagination, Select, Text } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getWidgetHeaders,
  getWidgetRows,
} from "src/components/pages/Home/CustomDashboard/Widget.utils";
import { useIsDev } from "src/hooks/useIsDev";

import HistoricalSnapshotPDFButton from "../../components/pages/HistoricalSnapshot/HistoricalSnapshotPDFButton";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "../../components/shared/Button/Button";
import Section from "../../components/shared/EntriesGenerator/Section/Section/Section";
import Loader from "../../components/shared/Loader/Loader";
import LogbookOverlay from "../../generator/LogbookOverlay/LogbookOverlay";
import { useGetApi } from "../../hooks";
import type {
  IHistoricalSnapshotData,
  ISnapshot,
} from "../../interfaces/HistoricalSnapshot";
import { useSelector } from "../../redux/hooks";
import { PageContainer } from "../../styled";
import { getYachtId } from "../../utils";

interface IResData extends IHistoricalSnapshotData {
  success: boolean;
  pagecount: number;
  layout?: number[][];
  pdfScale?: number;
}
interface Props {
  review?: boolean;
}

/**
 * Validates that the layout prop has consistent row sums
 * All rows must have the same total width sum
 * @param layout Array of arrays with column widths for each row
 * @returns true if valid, false otherwise
 */
const validateLayout = (layout: number[][], expected: number[]): boolean => {
  if (!Array.isArray(layout) || layout.length === 0) return false;

  if (expected.length > 0 && layout.length !== expected.length) return false;

  if (
    layout.some(
      (row, i) =>
        !Array.isArray(row) || row.length === 0 || row.length !== expected[i],
    )
  )
    return false;

  const rowSums = layout.map(row => {
    if (!Array.isArray(row) || row.length === 0) return null;
    if (
      row.some(
        ratio =>
          typeof ratio !== "number" || !Number.isFinite(ratio) || ratio <= 0,
      )
    ) {
      return null;
    }
    return row.reduce((sum, width) => sum + width, 0);
  });

  if (rowSums.some(sum => sum === null)) return false;

  const firstSum = rowSums[0];
  if (typeof firstSum !== "number" || firstSum <= 0) return false;
  return rowSums.every(sum => sum === firstSum);
};

const normalizeHeaders = (headers: any) => {
  if (Array.isArray(headers)) return headers;

  return Object.values(headers ?? {}).flat();
};

const createColumnMap = (headers: any) => {
  const normalizedHeaders = normalizeHeaders(headers);

  const map: Record<
    string,
    {
      path: string;
      type: string;
      name: string;
      unit: string;
      isSplit: boolean;
      isParentSplit: boolean;
    }
  > = {};

  let flatIndex = 0;
  normalizedHeaders.forEach((header, headerIndex) => {
    if (header.type === "split" && header.cols) {
      map[`col_${flatIndex}`] = {
        path: `${headerIndex}`,
        type: header.type,
        name: header.name || "",
        unit: header.unit || "",
        isSplit: true,
        isParentSplit: true,
      };
      flatIndex++;

      header.cols.forEach((subHeader: any, subIndex: number) => {
        map[`col_${flatIndex}`] = {
          path: `${headerIndex},${subIndex}`,
          type: subHeader.type || header.type,
          name: subHeader.name || "",
          unit: subHeader.unit || "",
          isSplit: true,
          isParentSplit: false,
        };
        flatIndex++;
      });
    } else {
      map[`col_${flatIndex}`] = {
        path: `${headerIndex}`,
        type: header.type || "col",
        name: header.name || "",
        unit: header.unit || "",
        isSplit: false,
        isParentSplit: false,
      };
      flatIndex++;
    }
  });

  return map;
};

const buildFullRowsSections = (
  previewData: IHistoricalSnapshotData,
  entry: any,
): Array<{
  headers: any[];
  rows: any[];
  title: string;
  colcount: number;
  className: string;
}> => {
  const sections: Array<{
    headers: any[];
    rows: any[];
    title: string;
    colcount: number;
    className: string;
  }> = [];

  const sectionEntries = Object.entries(previewData.headers ?? {});

  for (const [sectionKey, headers] of sectionEntries) {
    const rows = entry?.rows?.[sectionKey] ?? [];

    if (Array.isArray(headers) && Array.isArray(rows)) {
      sections.push({
        headers,
        rows,
        title: "",
        colcount: headers.length,
        className: "passive",
      });
    }
  }

  return sections;
};

const HistoricalSnapshot = ({ review }: Props) => {
  const isDev = useIsDev();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logbookId, aid } = useParams();
  const { navigatedFromLogbook } = useSelector(
    state => state.generatorFeaturesSlice,
  );
  const resultsRef = useRef<HTMLDivElement>(null);
  const [previewData, setPreviewData] = useState<IHistoricalSnapshotData>();
  const [sortedList, setSortedList] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [columnMap, setColumnMap] = useState<Record<string, any>>({});
  const [isLayoutValid, setIsLayoutValid] = useState(true);

  const { data, isLoading } = useGetApi<IResData>({
    request: review ? "get-preview-reviewer" : "get-preview",
    body: {
      params: {
        lbid: logbookId,
        yachtid: getYachtId(),
        page,
        pageMax: pageSize,
        ...(review && { aid }),
      },
    },
    fetchOnInit: true,
  });

  const layout = useMemo(
    () =>
      data?.layout ?? [
        [6, 5, 5, 12, 7, 5, 5, 5, 5, 5, 5, 6, 6, 6, 17],
        [6, 13, 16, 5, 5, 10, 10, 6, 6, 6, 11, 6],
      ], // NOTE REMOVE WHEN BACKEND PROVIDES LAYOUT
    [data?.layout],
  );

  const smallFont = useMemo(() => {
    if (!layout) return false;
    return layout.some(row => row.length > 10);
  }, [layout]);

  const expectedLayout = useMemo(() => {
    if (!layout || !data?.list || !previewData) return [];
    if (previewData?.snapshotFormat !== "fullRows") {
      return [data?.list?.[0]?.cols?.length ?? 0];
    }

    return Object.values(data?.list?.[0]?.rows ?? {}).map(r =>
      Array.isArray(r?.[0]) ? r?.[0]?.length : 0,
    );
  }, [layout, data?.list, previewData]);

  // Validate layout on component mount or when layout changes
  useEffect(() => {
    if (layout && expectedLayout.length > 0) {
      const isValid = validateLayout(layout, expectedLayout);
      setIsLayoutValid(isValid);
      if (!isValid) {
        console.warn(
          "Invalid layout provided. Please ensure all rows have the same total width sum and match the expected row count.",
          "\nExpected row count: ",
          expectedLayout,
          "\nReceived layout: ",
          layout,
          "\nCalculated ratio sums: ",
          layout.map(row => row.reduce((a, b) => a + b, 0)),
        );
      }
    }
  }, [layout, expectedLayout]);

  useEffect(() => {
    isDev &&
      data?.list &&
      data?.list.length > 0 &&
      console.log({ list: data.list });
  }, [data, isDev]);

  useEffect(() => {
    if (data?.success) {
      const isFullRowsSnapshot = data.snapshotFormat === "fullRows";
      const headers = ["Watchkeeper", "Events"].includes(data.lbname ?? "")
        ? getWidgetHeaders(data.headers, true)
        : data.headers;
      const list: ISnapshot[] =
        data.list.length > 0 &&
        typeof data?.list[0] === "object" &&
        "version" in data?.list[0]
          ? getWidgetRows(data.list)
          : data.list;
      const colMap = isFullRowsSnapshot ? {} : createColumnMap(headers);
      setColumnMap(colMap);

      setPreviewData({
        snapshotFormat: data.snapshotFormat,
        list,
        headers,
        lbname: data.lbname,
      });
      setSortedList(list);
    }
  }, [data, previewData?.snapshotFormat]);

  const handleHeaderClick = useCallback(
    (columnKey: string) => {
      if (previewData?.snapshotFormat === "fullRows") {
        return;
      }

      if (!columnMap[columnKey] || columnMap[columnKey].isParentSplit) {
        return;
      }

      const sortBy = searchParams.get("sort");
      const dir = searchParams.get("dir");

      if (sortBy === columnKey) {
        const newDir = dir === "asc" ? "desc" : "asc";
        setSearchParams({ sort: columnKey, dir: newDir }, { replace: true });
      } else {
        setSearchParams({ sort: columnKey, dir: "asc" }, { replace: true });
      }
    },
    [columnMap, previewData?.snapshotFormat, searchParams, setSearchParams],
  );

  const getValueFromPath = (row: any, columnInfo: any): any => {
    if (!columnInfo) return undefined;

    const path = columnInfo.path;
    const segments = path.split(",").map((s: string) => parseInt(s));
    const colIndex = segments[0];
    const cell = row.cols?.[colIndex];

    const value = cell?.value;

    if (
      columnInfo.unit === "sub" &&
      (value === undefined || value === "") &&
      cell?.content?.cols
    ) {
      const subCols = cell.content.cols;

      const textVal = subCols.find(
        (sc: any) => typeof sc?.value === "string" && sc.value.trim() !== "",
      )?.value;

      if (textVal) {
        return textVal;
      }

      const tankName = subCols.find(
        (sc: any) =>
          typeof sc?.value === "object" &&
          sc?.value?.data?.tankName?.trim?.() !== "",
      )?.value?.data?.tankName;

      if (tankName) {
        return tankName;
      }

      const key = subCols.find(
        (sc: any) =>
          typeof sc?.value === "object" &&
          typeof sc.value.key === "string" &&
          sc.value.key.trim() !== "",
      )?.value.key;

      if (key) {
        return key;
      }
    }

    if (segments.length === 2) {
      const [parentIdx, childIdx] = segments;
      const nested = row.cols?.[parentIdx]?.cols?.[childIdx]?.value;
      if (nested !== undefined) return nested;
    }

    const fallbackCol = row.cols.find(
      (col: any) =>
        col.name === columnInfo.name ||
        (columnInfo.unit && col.unit === columnInfo.unit),
    );
    if (fallbackCol?.value !== undefined) return fallbackCol.value;

    return value;
  };

  useEffect(() => {
    if (!previewData?.list || !columnMap) return;

    if (previewData.snapshotFormat === "fullRows") return;

    const sortBy = searchParams.get("sort");
    const dir = searchParams.get("dir") === "desc" ? -1 : 1;

    if (!sortBy || !columnMap[sortBy]) {
      setSortedList([...previewData.list]);
      return;
    }

    const columnInfo = columnMap[sortBy];

    const sorted = [...previewData.list].sort((a, b) => {
      let aValue = getValueFromPath(a, columnInfo);
      let bValue = getValueFromPath(b, columnInfo);

      const colType = columnInfo.unit;

      if (aValue && typeof aValue === "object") {
        if (colType === "date") {
          try {
            aValue = new Date(aValue).getTime();
          } catch (e) {
            aValue = 0;
          }
        } else if (colType === "time" && aValue.time) {
          aValue = aValue.time;
        } else if (
          ["user", "master", "userrefpop"].includes(colType) &&
          aValue.ref
        ) {
          aValue = parseInt(aValue.ref);
        } else {
          try {
            aValue = JSON.stringify(aValue);
          } catch (e) {
            aValue = "[object]";
          }
        }
      }

      if (bValue && typeof bValue === "object") {
        if (colType === "date") {
          try {
            bValue = new Date(bValue).getTime();
          } catch (e) {
            bValue = 0;
          }
        } else if (colType === "time" && bValue.time) {
          bValue = bValue.time;
        } else if (
          ["user", "master", "userrefpop"].includes(colType) &&
          bValue.ref
        ) {
          bValue = parseInt(bValue.ref);
        } else {
          try {
            bValue = JSON.stringify(bValue);
          } catch (e) {
            bValue = "[object]";
          }
        }
      }

      if (
        colType === "date" &&
        typeof aValue === "string" &&
        typeof bValue === "string"
      ) {
        try {
          const dateA = new Date(aValue).getTime();
          const dateB = new Date(bValue).getTime();
          return (dateA - dateB) * dir;
        } catch (e) {
          console.error("Invalid date format:", aValue, bValue);
          return 0;
        }
      }

      if (aValue === undefined || aValue === null) aValue = "";
      if (bValue === undefined || bValue === null) bValue = "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * dir;
      }

      return String(aValue).localeCompare(String(bValue)) * dir;
    });

    setSortedList(sorted);
  }, [searchParams, previewData?.list, previewData?.snapshotFormat, columnMap]);

  const createSortIndicator = (direction: string) => {
    const indicator = document.createElement("span");
    indicator.className = "sort-indicator";
    indicator.textContent = direction === "asc" ? " ↑" : " ↓";
    Object.assign(indicator.style, {
      position: "relative",
      display: "flex",
      justifyContent: "end",
      flexWrap: "wrap",
      alignItems: "top",
      alignSelf: "top",
      marginLeft: "1px",
      lineHeight: "1.2",
      fontSize: "1em",
      verticalAlign: "middle",
      zIndex: "1000",
    });
    return indicator;
  };

  useEffect(() => {
    if (!resultsRef.current || isLoading || !columnMap) return;

    if (previewData?.snapshotFormat === "fullRows") return;

    setTimeout(() => {
      const table = resultsRef.current?.querySelector("table");
      if (!table) return;

      const headerRows = table.querySelectorAll("thead tr");
      if (!headerRows.length) return;

      const sortBy = searchParams.get("sort");
      const dir = searchParams.get("dir");

      let flatIndex = 0;
      const processedCells = new Set<Element>();

      headerRows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll("th");

        cells.forEach((th, cellIndex) => {
          if (processedCells.has(th)) return;
          processedCells.add(th);

          const columnKey = `col_${flatIndex}`;
          const columnInfo = columnMap[columnKey];
          flatIndex++;

          if (!columnInfo) return;

          if (columnInfo.isParentSplit) {
            return;
          }

          if (
            th.getAttribute("colspan") &&
            parseInt(th.getAttribute("colspan") ?? "1") > 1
          ) {
            return;
          }

          th.style.cursor = "pointer";

          const newTh = th.cloneNode(true);
          th.parentNode?.replaceChild(newTh, th);

          newTh.addEventListener("click", () => {
            handleHeaderClick(columnKey);
          });

          if (columnKey === sortBy) {
            const existingIndicator = (newTh as Element).querySelector(
              ".sort-indicator",
            );
            if (existingIndicator) {
              existingIndicator.remove();
            }

            const indicator = createSortIndicator(dir ?? "asc");
            (newTh as Element).appendChild(indicator);
          }
        });
      });
    }, 200);
  }, [previewData, isLoading, searchParams, columnMap, handleHeaderClick]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const section = useMemo(
    () =>
      previewData
        ? {
            headers: previewData.headers,
            rows: sortedList,
            title: "",
            colcount: 0,
            className: "passive",
          }
        : null,
    [previewData, sortedList],
  );

  const isFullRowsSnapshot = previewData?.snapshotFormat === "fullRows";

  const fullRowsSections = useMemo(() => {
    if (!previewData || !isFullRowsSnapshot) return [];

    return sortedList.map((entry: any) =>
      buildFullRowsSections(previewData, entry),
    );
  }, [isFullRowsSnapshot, previewData, sortedList]);

  const fullRowsHeaderSections = fullRowsSections[0] ?? [];
  if (!previewData) return null;

  if (previewData.list.length === 0)
    return (
      <div style={{ width: "100%", marginTop: 40 }}>
        <Text ta="center">There is no older data for this logbook</Text>
      </div>
    );

  return (
    <PageContainer>
      <Flex gap={8} align="center" justify="space-between">
        {review ? (
          <Text size="lg">Historical Snapshot</Text>
        ) : (
          <Breadcrumbs border={false} />
        )}
        <Flex gap={12} align="center">
          {data?.pagecount && data?.pagecount > 1 ? (
            <Pagination
              mr={40}
              total={data?.pagecount}
              value={page}
              onChange={v => setPage(v)}
            />
          ) : null}
          <Text>Entries per page:</Text>
          <Select
            maw={130}
            defaultValue="25"
            allowDeselect={false}
            data={["25", "50", "100", "250"]}
            onChange={value => {
              setPageSize(parseInt(value ?? "25"));
              setPage(1);
            }}
          />
        </Flex>
      </Flex>
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        w="100%"
        mt={20}
        mb={20}>
        {navigatedFromLogbook && (
          <Button
            variant="outline"
            onClick={handleGoBack}
            leftSection={<IconChevronLeft />}>
            {review ? "Back to review" : "Back to entry"}
          </Button>
        )}
        {previewData.lbname && (
          <Text style={{ whiteSpace: "nowrap", fontWeight: 200 }}>
            {previewData.lbname}
          </Text>
        )}
        <Flex gap={8}>
          <HistoricalSnapshotPDFButton
            listRef={resultsRef}
            lbname={previewData.lbname}
            scale={data?.pdfScale ?? 0.75}
          />
        </Flex>
      </Flex>
      {isLoading ? (
        <Loader />
      ) : (
        <div
          ref={resultsRef}
          style={{ position: "relative", textAlign: "center" }}>
          {!isFullRowsSnapshot && section && (
            <LogbookOverlay sectionIndex={0} sections={[section as any]} />
          )}
          {isFullRowsSnapshot ? (
            <>
              {fullRowsHeaderSections.length > 0 && (
                <div
                  style={{
                    position: "sticky",
                    top: -15,
                    zIndex: 10,
                  }}>
                  {fullRowsHeaderSections.map((entrySection, sectionIndex) => (
                    <Section
                      key={`historical-snapshot-fixed-headers-section-${sectionIndex}`}
                      section={{ ...entrySection, rows: [] } as any}
                      historyRows={[]}
                      sIndex={sectionIndex}
                      horizontalScroll
                      snapshot
                      fullRows
                      hideHeaders={false}
                      smallFont={smallFont}
                      layout={
                        isLayoutValid && layout?.[sectionIndex]
                          ? layout[sectionIndex]
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
              {fullRowsSections.map((entrySections, entryIndex) => (
                <div
                  key={`historical-snapshot-entry-${entryIndex}`}
                  style={{ marginBottom: 20 }}>
                  {entrySections.map((entrySection, sectionIndex) => (
                    <Section
                      key={`historical-snapshot-entry-${entryIndex}-section-${sectionIndex}`}
                      section={entrySection as any}
                      historyRows={[]}
                      sIndex={sectionIndex}
                      horizontalScroll
                      snapshot
                      fullRows
                      hideHeaders={true}
                      smallFont={smallFont}
                      layout={
                        isLayoutValid && layout?.[sectionIndex]
                          ? layout[sectionIndex]
                          : undefined
                      }
                    />
                  ))}
                </div>
              ))}
            </>
          ) : (
            section && (
              <Section
                section={section as any}
                historyRows={[]}
                sIndex={0}
                horizontalScroll
                snapshot
                smallFont={smallFont}
                layout={
                  isLayoutValid && layout?.length === 1 ? layout[0] : undefined
                }
              />
            )
          )}
        </div>
      )}
    </PageContainer>
  );
};
export default HistoricalSnapshot;
